import { useEffect, useMemo, useState } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { adminApi } from "@/lib/api";
import type {
  ScienceLLM,
  Provider,
  ProviderModel,
  ScienceLLMProviderModel,
} from "@/lib/types";
import type {
  CreateScienceLLMInput,
  UpdateScienceLLMInput,
} from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Edit2, X, Plus, Route, Trash2, Search } from "lucide-react";

type ModalMode =
  | { type: "closed" }
  | { type: "create" }
  | { type: "edit"; sl: ScienceLLM }
  | { type: "routing"; sl: ScienceLLM };

export default function AdminScienceLLMs() {
  const { t } = useLanguage();
  const [sllms, setSllms] = useState<ScienceLLM[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerModels, setProviderModels] = useState<ProviderModel[]>([]);
  const [routingBySl, setRoutingBySl] = useState<Record<string, ScienceLLMProviderModel[]>>({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalMode>({ type: "closed" });

  const refresh = async () => {
    try {
      const slList = await adminApi.listScienceLLMs();
      setSllms(slList);

      const pList = await adminApi.getProviders();
      setProviders(pList);

      const pModelsList = await Promise.all(
        pList.map((p) => adminApi.getProviderModels(p.id))
      );
      const allPModels = pModelsList.flat();
      setProviderModels(allPModels);

      const entries = await Promise.all(
        slList.map(async (sl) => {
          try {
            const rows = await adminApi.getScienceLLMProviderModels(sl.id);
            return [sl.id, rows] as [string, ScienceLLMProviderModel[]];
          } catch {
            return [sl.id, [] as ScienceLLMProviderModel[]] as [string, ScienceLLMProviderModel[]];
          }
        })
      );
      const map: Record<string, ScienceLLMProviderModel[]> = {};
      for (const [id, rows] of entries) map[id] = rows;
      setRoutingBySl(map);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const pmById = useMemo(() => {
    const m = new Map<string, ProviderModel>();
    for (const row of providerModels) m.set(row.id, row);
    return m;
  }, [providerModels]);

  const pById = useMemo(() => {
    const m = new Map<string, Provider>();
    for (const row of providers) m.set(row.id, row);
    return m;
  }, [providers]);

  return (
    <ConsoleLayout
      title={t("admin.sciLLM.title")}
      subtitle={t("admin.sciLLM.subtitle")}
      actions={
        <Button
          onClick={() => setModal({ type: "create" })}
          className="h-9 px-4 btn-primary-sci gap-1.5"
        >
          <Plus size={14} />
          {t("admin.sciLLM.new")}
        </Button>
      }
    >
      <div className="p-6 space-y-4">
        <div className="sci-card overflow-hidden">
          <div className="px-6 py-5 border-b border-white/8 flex items-center justify-between">
            <h3 className="text-base font-semibold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
              {t("admin.sciLLM.title")} ({sllms.length})
            </h3>
          </div>
          <div className="divide-y divide-white/5">
            {sllms.map((sl) => {
              const routing = routingBySl[sl.id] ?? [];
              return (
                <div key={sl.id} className="px-6 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2.5 mb-2 flex-wrap">
                        <span className="text-base font-medium text-white font-mono">{sl.model_key}</span>
                        {!sl.is_active && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-500/15 text-red-400">
                            inactive
                          </span>
                        )}
                        <span className="text-sm text-slate-400">{sl.name}</span>
                      </div>
                      {sl.description && (
                        <div className="text-sm text-slate-500 mb-2 pl-0.5">{sl.description}</div>
                      )}
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500 mb-3">
                        <span>ctx: <span className="text-slate-200 font-mono">{sl.context_window}</span></span>
                        <span>tpm ceiling: <span className="text-slate-200 font-mono">{sl.tpm_limit ?? "—"}</span></span>
                        <span>rpm ceiling: <span className="text-slate-200 font-mono">{sl.rpm_limit ?? "—"}</span></span>
                      </div>

                      <div>
                        <div className="text-xs text-slate-400 font-semibold mb-1.5 tracking-wide">
                          {t("admin.sciLLM.routingSection")}
                        </div>
                        {routing.length > 0 ? (
                          <div className="pl-3 space-y-1">
                            {routing.map((r) => {
                              const pm = r.provider_model ?? pmById.get(r.provider_model_id);
                              const p = pm ? pById.get(pm.provider_id) : undefined;
                              return (
                                <div key={r.provider_model_id} className="flex items-center gap-3 text-sm flex-wrap">
                                  <span className="text-slate-200 font-mono">
                                    {pm?.name ?? r.provider_model_id}
                                  </span>
                                  {pm && p && (
                                    <>
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400">
                                        {p.name}
                                      </span>
                                      <span
                                        className="text-xs px-2 py-0.5 rounded-full font-mono bg-emerald-500/15 text-emerald-400"
                                      >
                                        {pm.protocol}
                                      </span>
                                    </>
                                  )}
                                  <span className="text-slate-500" title="当前路由策略为纯随机，weight 暂不影响选址顺序；weight > 0 仍作为软启用开关。">
                                    weight=<span className="text-slate-200 font-mono">{r.weight}</span>
                                    <span className="text-slate-500 text-[10px] ml-1">(已弃用)</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-sm text-slate-500 pl-3">{t("admin.sciLLM.routingEmpty")}</div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setModal({ type: "routing", sl })}
                        className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                        title={t("admin.sciLLM.editRouting")}
                      >
                        <Route size={16} />
                      </button>
                      <button
                        onClick={() => setModal({ type: "edit", sl })}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                        title={t("admin.sciLLM.editLimits")}
                      >
                        <Edit2 size={16} />
                      </button>
                      {sl.is_active && (
                        <button
                          onClick={async () => {
                            if (!confirm(t("admin.sciLLM.confirmDeactivate"))) return;
                            try {
                              await adminApi.deactivateScienceLLM(sl.id);
                              toast.success("Deactivated");
                              refresh();
                            } catch (err: any) {
                              toast.error(err?.response?.data?.message || "Deactivate failed");
                            }
                          }}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title={t("admin.sciLLM.deactivate")}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {sllms.length === 0 && !loading && (
              <div className="px-6 py-10 text-center text-base text-slate-500">
                {t("admin.sciLLM.empty")}
              </div>
            )}
          </div>
        </div>
      </div>

      {modal.type === "create" && (
        <CreateScienceLLMModal
          onClose={() => setModal({ type: "closed" })}
          onSaved={() => {
            setModal({ type: "closed" });
            refresh();
          }}
        />
      )}
      {modal.type === "edit" && (
        <EditScienceLLMModal
          sl={modal.sl}
          onClose={() => setModal({ type: "closed" })}
          onSaved={() => {
            setModal({ type: "closed" });
            refresh();
          }}
        />
      )}
      {modal.type === "routing" && (
        <RoutingModal
          sl={modal.sl}
          existing={routingBySl[modal.sl.id] ?? []}
          onClose={() => setModal({ type: "closed" })}
          onSaved={() => {
            setModal({ type: "closed" });
            refresh();
          }}
        />
      )}
    </ConsoleLayout>
  );
}

function CreateScienceLLMModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    model_key: "",
    description: "",
    protocol: "anthropic",
    context_window: 128000,
    tpm_limit: 0,
    rpm_limit: 0,
    input_st_per_ktok: 0,
    output_st_per_ktok: 0,
    cache_write_st_per_ktok: 0,
    cache_read_st_per_ktok: 0,
  });
  const [busy, setBusy] = useState(false);

  const setField = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.model_key.trim()) {
      toast.error("Name and model_key required");
      return;
    }
    if (form.context_window <= 0) {
      toast.error("Context window must be > 0");
      return;
    }
    setBusy(true);
    try {
      const payload: CreateScienceLLMInput = {
        name: form.name.trim(),
        model_key: form.model_key.trim(),
        description: form.description.trim() || undefined,
        protocol: form.protocol,
        context_window: Number(form.context_window),
        tpm_limit: form.tpm_limit > 0 ? Number(form.tpm_limit) : undefined,
        rpm_limit: form.rpm_limit > 0 ? Number(form.rpm_limit) : undefined,
        input_st_per_ktok: Number(form.input_st_per_ktok),
        output_st_per_ktok: Number(form.output_st_per_ktok),
        cache_write_st_per_ktok: Number(form.cache_write_st_per_ktok),
        cache_read_st_per_ktok: Number(form.cache_read_st_per_ktok),
      };
      await adminApi.createScienceLLM(payload);
      toast.success("ScienceLLM created");
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={t("admin.sciLLM.modalCreateTitle")} onClose={onClose}>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t("admin.sciLLM.fieldName")} full>
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="ScienceLLM-Pro"
            className="bg-white/5 border-white/10 text-white text-base h-10"
          />
        </Field>
        <Field label={t("admin.sciLLM.fieldKey")} full>
          <Input
            value={form.model_key}
            onChange={(e) => setField("model_key", e.target.value)}
            placeholder="sciencellm-pro"
            className="bg-white/5 border-white/10 text-white text-base h-10 font-mono"
          />
        </Field>
        <Field label={t("admin.sciLLM.fieldDesc")} full>
          <Textarea
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            rows={2}
            className="bg-white/5 border-white/10 text-white text-base"
          />
        </Field>
        <Field label="Protocol">
          <select
            value={form.protocol}
            onChange={(e) => setField("protocol", e.target.value)}
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-base text-white h-10"
          >
            <option value="anthropic">Anthropic</option>
            <option value="openai-chat">OpenAI Chat</option>
            <option value="openai-response">OpenAI Response</option>
          </select>
        </Field>
        <Field label={t("admin.sciLLM.fieldCtx")}>
          <NumberInput value={form.context_window} onChange={(n) => setField("context_window", n)} step={1000} />
        </Field>
        <Field label="TPM Ceiling" help="0 = unlimited (∞); team overrides cannot exceed this ceiling.">
          <NumberInput value={form.tpm_limit} onChange={(n) => setField("tpm_limit", n)} step={1000} />
        </Field>
        <Field label="RPM Ceiling" help="0 = unlimited (∞); team overrides cannot exceed this ceiling.">
          <NumberInput value={form.rpm_limit} onChange={(n) => setField("rpm_limit", n)} step={1} />
        </Field>
        <Field label="Input ST (per 1k)">
          <Input type="number" step="0.000001" value={form.input_st_per_ktok} onChange={(e) => setField("input_st_per_ktok", Number(e.target.value))} className="bg-white/5 border-white/10 text-white text-base h-10" />
        </Field>
        <Field label="Output ST (per 1k)">
          <Input type="number" step="0.000001" value={form.output_st_per_ktok} onChange={(e) => setField("output_st_per_ktok", Number(e.target.value))} className="bg-white/5 border-white/10 text-white text-base h-10" />
        </Field>
        <Field label="Cache Write ST (per 1k)">
          <Input type="number" step="0.000001" value={form.cache_write_st_per_ktok} onChange={(e) => setField("cache_write_st_per_ktok", Number(e.target.value))} className="bg-white/5 border-white/10 text-white text-base h-10" />
        </Field>
        <Field label="Cache Read ST (per 1k)">
          <Input type="number" step="0.000001" value={form.cache_read_st_per_ktok} onChange={(e) => setField("cache_read_st_per_ktok", Number(e.target.value))} className="bg-white/5 border-white/10 text-white text-base h-10" />
        </Field>
      </div>
      <ModalFooter busy={busy} onCancel={onClose} onSubmit={handleSubmit} submitLabel={t("admin.common.create")} />
    </ModalShell>
  );
}

function EditScienceLLMModal({ sl, onClose, onSaved }: { sl: ScienceLLM; onClose: () => void; onSaved: () => void }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: sl.name,
    description: sl.description ?? "",
    protocol: sl.protocol ?? "anthropic",
    context_window: sl.context_window,
    tpm_limit: sl.tpm_limit ?? 0,
    rpm_limit: sl.rpm_limit ?? 0,
    input_st_per_ktok: sl.input_st_per_ktok ?? 0,
    output_st_per_ktok: sl.output_st_per_ktok ?? 0,
    cache_write_st_per_ktok: sl.cache_write_st_per_ktok ?? 0,
    cache_read_st_per_ktok: sl.cache_read_st_per_ktok ?? 0,
  });
  const [busy, setBusy] = useState(false);

  const setField = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const payload: UpdateScienceLLMInput = {
        name: form.name,
        description: form.description,
        protocol: form.protocol,
        context_window: Number(form.context_window),
        tpm_limit: form.tpm_limit > 0 ? Number(form.tpm_limit) : 0,
        rpm_limit: form.rpm_limit > 0 ? Number(form.rpm_limit) : 0,
        input_st_per_ktok: Number(form.input_st_per_ktok),
        output_st_per_ktok: Number(form.output_st_per_ktok),
        cache_write_st_per_ktok: Number(form.cache_write_st_per_ktok),
        cache_read_st_per_ktok: Number(form.cache_read_st_per_ktok),
      };
      await adminApi.updateScienceLLM(sl.id, payload);
      toast.success("ScienceLLM updated");
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={t("admin.sciLLM.modalEditTitle")} onClose={onClose}>
      <ScienceLLMHeader sl={sl} />
      <div className="grid grid-cols-2 gap-4">
        <Field label={t("admin.sciLLM.fieldName")} full>
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className="bg-white/5 border-white/10 text-white text-base h-10"
          />
        </Field>
        <Field label={t("admin.sciLLM.fieldDesc")} full>
          <Textarea
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            rows={2}
            className="bg-white/5 border-white/10 text-white text-base"
          />
        </Field>
        <Field label="Protocol">
          <select
            value={form.protocol}
            onChange={(e) => setField("protocol", e.target.value)}
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-base text-white h-10"
          >
            <option value="anthropic">Anthropic</option>
            <option value="openai-chat">OpenAI Chat</option>
            <option value="openai-response">OpenAI Response</option>
          </select>
        </Field>
        <Field label={t("admin.sciLLM.fieldCtx")}>
          <NumberInput value={form.context_window} onChange={(n) => setField("context_window", n)} step={1000} />
        </Field>
        <Field label="TPM Ceiling" help="0 = unlimited (∞); team overrides cannot exceed this ceiling.">
          <NumberInput value={form.tpm_limit} onChange={(n) => setField("tpm_limit", n)} step={1000} />
        </Field>
        <Field label="RPM Ceiling" help="0 = unlimited (∞); team overrides cannot exceed this ceiling.">
          <NumberInput value={form.rpm_limit} onChange={(n) => setField("rpm_limit", n)} step={1} />
        </Field>
        <Field label="Input ST (per 1k)">
          <Input type="number" step="0.000001" value={form.input_st_per_ktok} onChange={(e) => setField("input_st_per_ktok", Number(e.target.value))} className="bg-white/5 border-white/10 text-white text-base h-10" />
        </Field>
        <Field label="Output ST (per 1k)">
          <Input type="number" step="0.000001" value={form.output_st_per_ktok} onChange={(e) => setField("output_st_per_ktok", Number(e.target.value))} className="bg-white/5 border-white/10 text-white text-base h-10" />
        </Field>
        <Field label="Cache Write ST (per 1k)">
          <Input type="number" step="0.000001" value={form.cache_write_st_per_ktok} onChange={(e) => setField("cache_write_st_per_ktok", Number(e.target.value))} className="bg-white/5 border-white/10 text-white text-base h-10" />
        </Field>
        <Field label="Cache Read ST (per 1k)">
          <Input type="number" step="0.000001" value={form.cache_read_st_per_ktok} onChange={(e) => setField("cache_read_st_per_ktok", Number(e.target.value))} className="bg-white/5 border-white/10 text-white text-base h-10" />
        </Field>
      </div>
      <ModalFooter busy={busy} onCancel={onClose} onSubmit={handleSubmit} submitLabel={t("admin.common.update")} />
    </ModalShell>
  );
}

interface RoutingRow {
  provider_model_id: string;
  include: boolean;
  weight: number;
}

function RoutingModal({
  sl,
  existing,
  onClose,
  onSaved,
}: {
  sl: ScienceLLM;
  existing: ScienceLLMProviderModel[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const PAGE_SIZE = 20;

  // Edits keyed by provider_model_id: seeded from existing bindings, mutated
  // as the user toggles include/weight across paginated search results.
  const [edits, setEdits] = useState<Record<string, RoutingRow>>(() => {
    const m: Record<string, RoutingRow> = {};
    for (const e of existing) {
      m[e.provider_model_id] = {
        provider_model_id: e.provider_model_id,
        include: true,
        weight: e.weight ?? 1,
      };
    }
    return m;
  });

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<{ provider_models: ProviderModel[]; total: number; page: number; page_size: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await adminApi.searchProviderModels({ q: query.trim() || undefined, page, page_size: PAGE_SIZE });
        if (!cancelled) setResult(r);
      } catch (err: any) {
        if (!cancelled) toast.error(err?.response?.data?.message || "Search failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, page]);

  const setEdit = (id: string, patch: Partial<RoutingRow>) => {
    setEdits((prev) => {
      const cur = prev[id] ?? { provider_model_id: id, include: false, weight: 1 };
      return { ...prev, [id]: { ...cur, ...patch } };
    });
  };

  const totalPages = result ? Math.max(1, Math.ceil(result.total / result.page_size)) : 1;

  const handleSubmit = async () => {
    const picked = Object.values(edits).filter((r) => r.include);
    for (const r of picked) {
      if (r.weight <= 0) {
        toast.error("Weight must be > 0");
        return;
      }
    }
    setBusy(true);
    try {
      const payload = picked.map((r) => ({
        provider_model_id: r.provider_model_id,
        weight: Number(r.weight),
      }));
      await adminApi.setScienceLLMProviderModels(sl.id, payload);
      toast.success("Routing updated");
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const rows = result?.provider_models ?? [];

  return (
    <ModalShell title={t("admin.sciLLM.modalRoutingTitle")} onClose={onClose} maxWidth="800px">
      <ScienceLLMHeader sl={sl} />
      <p className="text-sm text-slate-400 mb-4">{t("admin.sciLLM.routingHint")}</p>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder="Search provider, model, or protocol…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              className="pl-9 bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600"
            />
          </div>
          <span className="text-xs text-slate-500 ml-auto">
            {result ? `${result.total} result${result.total === 1 ? "" : "s"}` : ""}
          </span>
        </div>

        <div className="border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-xs font-semibold text-slate-400 tracking-wider">
                <th className="px-3 py-2.5 w-16">{t("admin.sciLLM.colInclude")}</th>
                <th className="px-3 py-2.5">Provider</th>
                <th className="px-3 py-2.5">Model</th>
                <th className="px-3 py-2.5">{t("admin.sciLLM.colProtocols")}</th>
                <th className="px-3 py-2.5 w-28 text-left font-medium text-slate-300">
                  <span title="当前路由策略为纯随机，weight 暂不影响选址顺序；weight > 0 仍作为软启用开关。">
                    {t("admin.sciLLM.colWeight")} <span className="text-slate-500 text-[10px] ml-1">(已弃用)</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">
                    {query ? `No provider models match "${query}".` : "No active provider models available."}
                  </td>
                </tr>
              ) : (
                rows.map((u) => {
                  const edit = edits[u.id] ?? { provider_model_id: u.id, include: false, weight: 1 };
                  const p = u.provider;
                  return (
                    <tr key={u.id} className={edit.include ? "" : "opacity-60"}>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={edit.include}
                          onCheckedChange={(v) => setEdit(u.id, { include: !!v })}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400">
                          {p?.name ?? u.provider_id}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-slate-200">{u.name}</div>
                        <div className="text-xs text-slate-500 font-mono">→ {u.upstream_model_name}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs px-2 py-0.5 rounded-full font-mono bg-emerald-500/15 text-emerald-400">
                            {u.protocol}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min={1}
                          value={edit.weight}
                          disabled={!edit.include}
                          onChange={(e) => setEdit(u.id, { weight: Number(e.target.value) })}
                          title="当前路由策略为纯随机，weight 暂不影响选址顺序；weight > 0 仍作为软启用开关。"
                          className="bg-white/5 border-white/10 text-white text-sm h-8 w-20"
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {result && result.total > result.page_size && (
          <div className="flex items-center justify-between text-xs text-slate-400">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      <ModalFooter
        busy={busy}
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitLabel={t("admin.common.update")}
      />
    </ModalShell>
  );
}

function ScienceLLMHeader({ sl }: { sl: ScienceLLM }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-slate-400">
      <span>model_key:</span>
      <code className="font-mono text-slate-200 text-base">{sl.model_key}</code>
    </div>
  );
}

function ModalShell({
  title,
  children,
  onClose,
  maxWidth = "640px",
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  maxWidth?: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full mx-4 sci-card p-7 max-h-[90vh] overflow-y-auto"
        style={{ maxWidth }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({
  busy,
  onCancel,
  onSubmit,
  submitLabel,
  submitDisabled,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitDisabled?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div className="flex gap-3 pt-6">
      <Button variant="ghost" onClick={onCancel} className="flex-1 h-11 text-base text-slate-400 hover:text-white border border-white/10">
        {t("admin.common.cancel")}
      </Button>
      <Button
        onClick={onSubmit}
        disabled={busy || submitDisabled}
        className="flex-1 h-11 text-base btn-primary-sci"
      >
        {busy ? "..." : submitLabel}
      </Button>
    </div>
  );
}

function Field({
  label,
  children,
  full,
  help,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
  help?: string;
}) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-sm text-slate-300 mb-2 block">
        {label}
        {help && <span className="block text-[10px] text-slate-500 font-normal mt-0.5">{help}</span>}
      </Label>
      {children}
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  step,
}: {
  value: number;
  onChange: (n: number) => void;
  step?: number;
}) {
  return (
    <Input
      type="number"
      step={step ?? 1}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="bg-white/5 border-white/10 text-white text-base h-10"
    />
  );
}
